import classNames from 'classnames';
import type { TFunction } from 'i18next';
import { Observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';

import type { Goal } from '@feathr/blackbox';
import { type IColumn, Pill, TableColumnHeader, Text, Time } from '@feathr/components';
import { goalTypeLabels } from '@feathr/extender/components/Goal/Goal.predicates';
import { TimeFormat } from '@feathr/hooks';

import { formatGoalValue } from '../ReportsConversionsGoalManagerPage.utils';
import GoalManagerOptions from './GoalManagerOptions';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';

function goalManagerTableColumns(t: TFunction): Array<IColumn<Goal>> {
  return [
    {
      id: 'name',
      Header: TableColumnHeader({ sortType: 'alpha', title: t('Goal Name') }),
      headerClassName: classNames(tableStyles.cellLeft, tableStyles.sort),
      sortable: true,
      Cell({ original }): JSX.Element {
        return (
          <Observer>
            {function useAnonymousFunction(): JSX.Element {
              return <Text.DefaultRegular>{original.get('name')}</Text.DefaultRegular>;
            }}
          </Observer>
        );
      },
    },
    {
      id: 'conv_type',
      Header: TableColumnHeader({ sortType: 'alpha', title: t('Type') }),
      headerClassName: tableStyles.sort,
      sortable: true,
      Cell({ original }): JSX.Element {
        return (
          <Observer>
            {function useAnonymousFunction(): JSX.Element {
              const convType = original.get('conv_type');
              return (
                <Text.DefaultRegular>{goalTypeLabels[convType] ?? convType}</Text.DefaultRegular>
              );
            }}
          </Observer>
        );
      },
    },
    {
      id: 'conv_value',
      Header: TableColumnHeader({ sortType: 'numeric', title: t('Value') }),
      headerClassName: tableStyles.sort,
      sortable: true,
      width: 120,
      Cell({ original }): JSX.Element {
        return (
          <Observer>
            {function useAnonymousFunction(): JSX.Element {
              return <Text.DefaultRegular>{formatGoalValue(original, t)}</Text.DefaultRegular>;
            }}
          </Observer>
        );
      },
    },
    {
      id: 'num_conversions',
      Header: TableColumnHeader({ sortType: 'numeric', title: t('Conversions') }),
      headerClassName: tableStyles.sort,
      sortable: true,
      width: 130,
      Cell(): JSX.Element {
        // TODO: Read num_conversions from AggregatedStats once goal stats endpoint is implemented.
        return <Text.DefaultRegular>—</Text.DefaultRegular>;
      },
    },
    {
      id: 'total_conversion_value',
      Header: TableColumnHeader({ sortType: 'numeric', title: t('Conversion Value') }),
      headerClassName: tableStyles.sort,
      sortable: true,
      width: 160,
      Cell(): JSX.Element {
        // TODO: Compute total value (from AggregatedStats) once goal stats endpoint is implemented.
        return <Text.DefaultRegular>—</Text.DefaultRegular>;
      },
    },
    {
      id: 'date_last_modified',
      Header: TableColumnHeader({ sortType: 'numeric', title: t('Last Edited') }),
      headerClassName: tableStyles.sort,
      sortable: true,
      width: 150,
      Cell({ original }): JSX.Element {
        return (
          <Observer>
            {function useAnonymousFunction(): JSX.Element {
              return (
                <Time format={TimeFormat.longDate} timestamp={original.get('date_last_modified')} />
              );
            }}
          </Observer>
        );
      },
    },
    {
      id: 'status',
      Header: TableColumnHeader({ title: t('Status') }),
      sortable: false,
      width: 110,
      Cell({ original }): JSX.Element {
        return (
          <Observer>
            {function useAnonymousFunction(): JSX.Element {
              const isArchived = original.get('is_archived');
              return isArchived ? (
                <Pill theme={'neutral'} variant={'secondary'}>
                  {t('Archived')}
                </Pill>
              ) : (
                <Pill theme={'cyan'} variant={'secondary'}>
                  {t('Active')}
                </Pill>
              );
            }}
          </Observer>
        );
      },
    },
    {
      id: 'options',
      Header: '',
      sortable: false,
      width: 80,
      Cell({ original }): JSX.Element {
        return (
          <div className={tableStyles.cellCenter}>
            <Observer>
              {function useAnonymousFunction(): JSX.Element {
                return <GoalManagerOptions goal={original} />;
              }}
            </Observer>
          </div>
        );
      },
    },
  ];
}

export default goalManagerTableColumns;
